import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import styles from "./ProgramNav.module.css";
import { useRouter } from "next/router";

interface ProgramNavProps {
  className?: string;
  cmsData?: any;
  commonData?: any;
}

type SectionMatch = {
  id: string;
  distance: number;
};

const ProgramNav: React.FC<ProgramNavProps> = ({ className, cmsData, commonData }) => {
  const [activeSection, setActiveSection] = useState<string>("overview");
  const [isSticky, setIsSticky] = useState(false);
  const router = useRouter();
  const { slug } = router.query;
  const navRefs = useRef<{ [key: string]: HTMLLIElement | null }>({});

  useEffect(() => {
    setActiveSection("overview");
  }, [slug]);

  // Check if section exists in DOM
  const checkSectionExists = (id: string): boolean => {
    if (typeof window === "undefined") return false;
    const element = document.getElementById(id);
    if (element) {
      // Check if element has content (not empty)
      return element.children.length > 0 || element.textContent?.trim().length > 0;
    }
    return false;
  };

  const allSections = useMemo(() => [
    { 
      id: "overview", 
      label: "Overview",
      hasData: !!cmsData?.sections?.overiew_section
    },
    { 
      id: "core-modules", 
      label: "Core Modules",
      hasData: !!cmsData?.sections?.core_modules
    },
    { 
      id: "certificate", 
      label: "Certificate",
      hasData: !!cmsData?.certificate_section && cmsData?.certificate_section?.hide_section !== true
    }, // Component: CourseCertificate - @/components/CourseCertificate/CourseCertificate
    { 
      id: "projects", 
      label: "Projects",
      hasData: !!cmsData?.projects_section && cmsData?.projects_section?.hide_section !== true
    }, // Component: ProgrammeExploreFuture - @/components/ProgrammeExploreFuture/ProgrammeExploreFuture
    { 
      id: "why-choose", 
      label: "Why Choose",
      hasData: !!cmsData?.why_choose_this_program_section && cmsData?.why_choose_this_program_section?.hide_section !== true
    }, // Component: ProgrammeWhyChoose - @/components/ProgrammeWhyChoose/ProgrammeWhyChoose
    { 
      id: "admission-process", 
      label: "Admission Process",
      hasData: !!cmsData?.sections?.admission_process
    },
    { 
      id: "faculty", 
      label: "Faculty Spotlights",
      hasData: !!cmsData?.faculty_addition_section && cmsData?.faculty_addition_section?.hide_section !== true
    }, // Component: CourseFacultyLights - @/components/CourseFacultyLights/CourseFacultyLights
    { 
      id: "why-regenesys", 
      label: "Key Takeaways",
      hasData: !!cmsData?.why_regenesys_section && cmsData?.why_regenesys_section?.hide_section !== true
    }, // Component: ProgrammeWhyRegenesys - @/components/ProgrammeWhyRegenesys/ProgrammeWhyRegenesys
    { 
      id: "financial-aid", 
      label: "Financial Aid",
      hasData: !!(cmsData?.sections?.financial_aid?.student_funding || commonData?.program_student_funding_section)
    },
    { 
      id: "testimonials", 
      label: "Testimonials",
      hasData: !!cmsData?.sections?.testimonial
    }, // Component: Testimonials - @/components/Testimonials/Testimonials
    { 
      id: "masterclass", 
      label: "Masterclass",
      hasData: !!cmsData?.masterclass && cmsData?.masterclass?.hide_section !== true
    } // Component: ProgrammeEvents - @/components/ProgrammeEvents/ProgrammeEvents
  ], [cmsData, commonData]);

  const [sections, setSections] = useState(() => {
    // Initial filter based on data availability
    return allSections.filter((section) => {
      if (["overview", "core-modules", "admission-process"].includes(section.id)) {
        return section.hasData;
      }
      return section.hasData;
    }).map((section) => ({ id: section.id, label: section.label }));
  });

  // Update sections after DOM is ready to check for elements
  useEffect(() => {
    if (typeof window !== "undefined") {
      // Small delay to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        const filteredSections = allSections.filter((section) => {
          if (["overview", "core-modules", "admission-process"].includes(section.id)) {
            return section.hasData;
          }
          // For optional sections, check both data and DOM existence
          return section.hasData || checkSectionExists(section.id);
        }).map((section) => ({ id: section.id, label: section.label }));
        
        setSections(filteredSections);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [allSections]);
  const [isMobile, setIsMobile] = useState(false);
  const isLpPage = router.pathname.includes("/lp");
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkIfMobile();

    window.addEventListener("resize", checkIfMobile);

    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  const scrollToSection = (id: string) => {
    const section = document.getElementById(id);
    const offset = isLpPage ? (isMobile ? 70 : 100) : 180;

    if (section) {
      const topPosition =
        section.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: topPosition, behavior: "smooth" });
    }
  };

  const handleScroll = useCallback(() => {
    // Check if nav is sticky
    const nav = document.querySelector(`.${styles.stickyNav}`);
    if (nav) {
      const rect = nav.getBoundingClientRect();
      setIsSticky(rect.top <= 0);
    }

    // Update active section based on viewport visibility
    const viewportTop = window.scrollY;
    const viewportBottom = window.scrollY + window.innerHeight;
    const offset = isLpPage ? (isMobile ? 70 : 100) : 180;
    const threshold = viewportTop + offset + 50; // Offset for sticky nav
    
    // Find the section that is currently in viewport
    let activeSectionId = sections[0]?.id || "overview";
    let bestMatch: SectionMatch | null = null;
    let fallbackMatch: SectionMatch | null = null;

    // Check all sections (including those not in nav) to find the active one
    allSections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) {
        const rect = element.getBoundingClientRect();
        const elementTop = rect.top + window.scrollY;
        const elementBottom = elementTop + rect.height;
        const distance = Math.abs(elementTop - threshold);
        
        // Check if section is in viewport (with threshold offset)
        const isSectionPassed = elementTop <= threshold;
        const isSectionVisible = elementBottom >= viewportTop && elementTop <= viewportBottom;
        
        if (isSectionPassed && isSectionVisible) {
          // Prioritize sections that are passed and visible
          if (bestMatch === null || distance < bestMatch.distance) {
            bestMatch = { id: section.id, distance };
          }
        } else {
          // Keep track of closest section as fallback
          if (fallbackMatch === null || distance < fallbackMatch.distance) {
            fallbackMatch = { id: section.id, distance };
          }
        }
      }
    });

    // Use best match if found, otherwise use fallback
    if (bestMatch !== null) {
      activeSectionId = (bestMatch as SectionMatch).id;
    } else if (fallbackMatch !== null) {
      activeSectionId = (fallbackMatch as SectionMatch).id;
    }

    setActiveSection(activeSectionId);
  }, [sections, allSections, isLpPage, isMobile]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", handleScroll);
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleScroll]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const activeRef = navRefs.current[activeSection];
      if (activeRef) {
        // Scroll active tab into view if it's not visible (works for all screen sizes)
        const navList = activeRef.closest(`.${styles.navList}`);
        if (navList) {
          const navRect = navList.getBoundingClientRect();
          const itemRect = activeRef.getBoundingClientRect();
          
          // Check if item is outside visible area
          const isOutOfView = 
            itemRect.left < navRect.left || 
            itemRect.right > navRect.right;
          
          if (isOutOfView) {
            activeRef.scrollIntoView({
              behavior: "smooth",
              inline: "center",
              block: "nearest",
            });
          }
        }
      }
    }
  }, [activeSection]);

  return (
    <nav
      className={
        isLpPage
          ? `${styles.stickyNav} ${styles.stickyNavLp} ${className || ""} ${
              isSticky ? "is-sticky" : ""
            }`
          : `${styles.stickyNav} ${className || ""} ${
              isSticky ? "is-sticky" : ""
            }`
      }
    >
      <ul className={styles.navList}>
        {sections?.map((section) => (
          <li
            key={section?.id}
            ref={(el) => {
              navRefs.current[section.id] = el;
            }}
            onClick={() => scrollToSection(section?.id)}
            className={
              activeSection === section?.id
                ? `${styles.navItem} ${styles.active}`
                : styles.navItem
            }
          >
            {section?.label}
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default ProgramNav;
